import type { ReactNode } from 'react';
import { AlertCircle, Inbox } from 'lucide-react';

interface LoadingStateProps {
  message?: string;
}

export function LoadingState({ message = '데이터를 불러오는 중...' }: LoadingStateProps) {
  return (
    <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-12">
      <div className="text-center">
        <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-primary-500" />
        <p className="text-sm text-gray-600">{message}</p>
      </div>
    </div>
  );
}

interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
}

export function ErrorState({ error, onRetry }: ErrorStateProps) {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '알 수 없는 오류가 발생했습니다.';
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-6">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
        <div className="flex-1">
          <h3 className="text-sm font-medium text-red-800">
            데이터를 불러오는데 실패했습니다.
          </h3>
          <p className="mt-1 text-sm text-red-700">{message}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-3 rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
            >
              다시 시도
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface EmptyStateProps {
  message?: string;
  description?: ReactNode;
}

export function EmptyState({
  message = '데이터가 없습니다',
  description,
}: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-white p-12 text-center">
      <Inbox className="mb-3 h-10 w-10 text-gray-300" />
      <p className="text-sm font-medium text-gray-700">{message}</p>
      {description && (
        <p className="mt-1 text-xs text-gray-500">{description}</p>
      )}
    </div>
  );
}

/**
 * 페이지 위에 살짝 떠 있는 로딩 인디케이터 (재요청 중에 콘텐츠는 유지하고 싶을 때)
 */
export function InlineFetchingOverlay() {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/40 backdrop-blur-[1px]">
      <div className="rounded-full bg-white px-3 py-1.5 text-xs text-gray-600 shadow">
        불러오는 중...
      </div>
    </div>
  );
}
